import { X } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import SeekerLogo from '../../assets/svg/seeker-logo.svg?react';
import { getDappHomePath } from '../../utils/dappRoutes';
import { Button } from '../Button';
import { ConfirmationDialog } from '../ConfirmationDialog';
import { useDappContext } from '../DappLayout';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

interface NewVersionPageHeaderProps {
  isFormTouched: boolean;
  isFormValid: boolean;
  isSubmitting?: boolean;
  hasError?: boolean;
  isSubmitDisabled?: boolean;
  disabledTooltip?: string;
}

export function NewVersionPageHeader({
  isFormTouched,
  isFormValid,
  isSubmitting = false,
  hasError = false,
  isSubmitDisabled,
  disabledTooltip,
}: NewVersionPageHeaderProps) {
  const navigate = useNavigate();
  const { dappId, packageName } = useDappContext();
  const homePath = getDappHomePath({ id: dappId, packageName });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const submitDisabled =
    typeof isSubmitDisabled === 'boolean'
      ? isSubmitDisabled
      : !isFormValid || isSubmitting || hasError;

  const handleClose = () => {
    if (isFormTouched) {
      setShowConfirmDialog(true);
    } else {
      navigate(homePath);
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmDialog(false);
    navigate(homePath);
  };

  const handleCancelClose = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <div className="flex h-16 shrink-0 items-center justify-between rounded-t-xl bg-seeker-800 px-6 text-white">
        <button
          type="button"
          aria-label="Close"
          className="flex size-12 items-center justify-center rounded-xl transition-colors duration-150 hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          onClick={handleClose}
        >
          <X className="size-6" />
        </button>
        <SeekerLogo
          aria-label="Seeker"
          className="h-7 w-auto shrink-0 fill-current"
        />
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Button
                  type="submit"
                  form="version-form"
                  variant="primary"
                  disabled={submitDisabled}
                  submitting={isSubmitting}
                  className="min-w-[120px]"
                >
                  Submit
                </Button>
              </span>
            </TooltipTrigger>
            {disabledTooltip ? (
              <TooltipContent>
                <p>{disabledTooltip}</p>
              </TooltipContent>
            ) : null}
          </Tooltip>
        </TooltipProvider>
      </div>

      <ConfirmationDialog
        isOpen={showConfirmDialog}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave this page?"
        confirmText="Leave"
        cancelText="Stay"
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}
